import { useEffect, useState } from 'react'
import { api, type AdminLogEntry, type AdminLogs as AdminLogsData } from './api'
import Pager from './Pager'

const CATEGORIES = [
  { key: 'all', label: 'Everything' },
  { key: 'content', label: 'Posts & contributors' },
  { key: 'access', label: 'Sign-ins & team' },
  { key: 'audience', label: 'Newsletter & contact' },
  { key: 'problems', label: 'Warnings & errors' },
  { key: 'system', label: 'System' },
] as const

const EVENT_LABELS: Record<string, string> = {
  post: 'Post',
  contributor: 'Contributor',
  upload: 'Upload',
  login: 'Sign-in',
  logout: 'Sign-out',
  team: 'Team',
  newsletter: 'Newsletter',
  contact: 'Contact',
  boot: 'System',
  api: 'System',
  ssr: 'System',
  seed: 'System',
}

function levelClass(level: AdminLogEntry['level']) {
  if (level === 'error') return 'text-rose'
  if (level === 'warn') return 'text-stone'
  return 'text-muted'
}

function formatTime(ms: number) {
  return new Date(ms).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

export default function AdminLogs() {
  const [category, setCategory] = useState<string>('all')
  const [page, setPage] = useState(1)
  const [data, setData] = useState<AdminLogsData | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    api<AdminLogsData>(`/api/admin/logs?category=${encodeURIComponent(category)}&page=${page}`)
      .then((next) => {
        if (cancelled) return
        setData(next)
        setError('')
        if (next.page !== page) setPage(next.page)
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message)
      })
    return () => {
      cancelled = true
    }
  }, [category, page])

  return (
    <section>
      <p className="text-caps text-[10px] text-rose">Site</p>
      <h2 className="mt-1 font-serif text-[32px] leading-none font-medium">Logs</h2>
      <p className="mt-3 max-w-[56ch] text-sm text-muted">
        What happened in the studio and on the site, newest first: posts, sign-ins, team changes, newsletter sign-ups
        and errors. Entries older than 30 days are cleared automatically.
      </p>

      <div className="mt-6 flex flex-wrap gap-2" role="tablist" aria-label="Filter logs">
        {CATEGORIES.map((item) => (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={category === item.key}
            onClick={() => {
              setCategory(item.key)
              setPage(1)
            }}
            className={`text-caps h-8 border px-3 text-[10px] transition-colors ${
              category === item.key ? 'border-ink bg-ink text-white' : 'border-rule text-muted hover:border-ink hover:text-ink'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {error ? <p className="mt-6 text-sm text-rose">{error}</p> : null}
      {!data && !error ? <p className="mt-6 text-muted">Loading logs…</p> : null}

      {data ? (
        data.logs.length === 0 ? (
          <p className="mt-6 text-sm text-muted">Nothing here yet.</p>
        ) : (
          <>
            <ul className="mt-6 divide-y divide-rule border-y border-rule">
              {data.logs.map((log) => (
                <li key={log.id} className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5 py-2.5 sm:grid-cols-[7.5rem_7rem_1fr_auto]">
                  <span className="text-[12px] text-muted tabular-nums sm:order-none">{formatTime(log.createdAt)}</span>
                  <span className={`text-caps text-[10px] leading-[18px] ${levelClass(log.level)}`}>
                    {log.level === 'info' ? EVENT_LABELS[log.event] || log.event : `${log.level} · ${EVENT_LABELS[log.event] || log.event}`}
                  </span>
                  <span className="col-span-2 min-w-0 text-[14px] break-words sm:col-span-1">{log.message}</span>
                  <span className="col-span-2 min-w-0 truncate text-[12px] text-muted sm:col-span-1 sm:text-right">
                    {log.actor || ''}
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-[12px] text-muted">{data.total.toLocaleString()} entries</p>
            <Pager page={data.page} pages={data.pages} onPage={setPage} />
          </>
        )
      ) : null}
    </section>
  )
}
